import styled, { keyframes } from 'styled-components'

const loadingShimmer = keyframes`
  to { transform: translateX(100%); }
`

export const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`

export const StyledIframe = styled.iframe<{ $isLoaded: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
  z-index: 2;
  background-color: #fff;
  opacity: ${({ $isLoaded }) => $isLoaded ? 1 : 0};
  pointer-events: ${({ $isLoaded }) => $isLoaded ? 'auto' : 'none'};
  transition: opacity 160ms ease-out;
`

export const NoraLoadingShell = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: grid;
  grid-template-rows: 76px minmax(0, 1fr) 126px;
  overflow: hidden;
  color: #253044;
  background: #fff;

  .nora-loading-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 0 72px 0 28px;
    border-bottom: 1px solid #dbe4f5;
  }

  .nora-loading-header img {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    object-fit: cover;
  }

  .nora-loading-header strong {
    color: #3264ef;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .nora-loading-conversation {
    min-height: 0;
    padding: 28px 32px;
    background: #fff;
  }

  .nora-loading-assistant-message {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr);
    gap: 18px;
    align-items: start;
  }

  .nora-loading-assistant-mark {
    width: 17px;
    height: 17px;
    margin: 14px 0 0 2px;
    border-radius: 4px;
    background: #0b50b5;
    transform: rotate(45deg);
  }

  .nora-loading-message-bubble {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 13px;
    min-height: 88px;
    padding: 20px 22px;
    border-radius: 24px;
    background: #f6f7f9;
    box-shadow: 0 2px 5px rgba(20, 35, 60, 0.12);
  }

  .nora-loading-line {
    position: relative;
    display: block;
    overflow: hidden;
    height: 14px;
    border-radius: 999px;
    background: #e2e6ed;
  }

  .nora-loading-line::after {
    position: absolute;
    inset: 0;
    background: linear-gradient(100deg, transparent 20%, rgba(255, 255, 255, 0.86) 50%, transparent 80%);
    content: '';
    transform: translateX(-100%);
  }

  .nora-loading-line--long {
    width: 88%;
  }

  .nora-loading-line--medium {
    width: 58%;
  }

  .nora-loading-user-message {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 92px;
    height: 58px;
    margin: 44px 0 0 auto;
    border-radius: 22px;
    background: #d9e7fa;
    box-shadow: 0 2px 5px rgba(20, 35, 60, 0.1);
  }

  .nora-loading-line--user {
    width: 44px;
    height: 12px;
    background: #b6cbea;
  }

  .nora-loading-thinking {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 54px 0 0 46px;
  }

  .nora-loading-thinking-mark {
    width: 24px;
    height: 24px;
    border: 3px solid #9cbce6;
    border-radius: 50%;
    box-shadow: inset 0 0 0 4px #fff;
    background: #1756ae;
  }

  .nora-loading-line--thinking {
    width: 90px;
    height: 12px;
    background: #c6d6ec;
  }

  .nora-loading-composer {
    position: relative;
    padding: 20px 24px 10px;
    border-top: 1px solid #e2e8f4;
    background: #fff;
    pointer-events: none;
    user-select: none;
  }

  .nora-loading-controls {
    display: grid;
    grid-template-columns: 160px minmax(0, 1fr) 24px 48px;
    gap: 12px;
    align-items: center;
  }

  .nora-loading-profile-select,
  .nora-loading-input {
    display: flex;
    align-items: center;
    box-sizing: border-box;
    height: 52px;
    border-radius: 28px;
    background: #f6f7f9;
    cursor: not-allowed;
  }

  .nora-loading-profile-select {
    justify-content: space-between;
    padding: 0 18px;
    border: 1px solid #dce1e9;
    color: #7d8796;
    font-size: 14px;
    font-weight: 600;
    text-align: left;
  }

  .nora-loading-input {
    width: 100%;
    padding: 0 18px;
    border: 1px solid #dce1e9;
    outline: none;
    color: #929aa8;
    font-size: 16px;
  }

  .nora-loading-input::placeholder {
    color: #a6adba;
    opacity: 1;
  }

  .nora-loading-mic {
    color: #c3c7cd;
  }

  .nora-loading-send {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    padding: 0;
    border: none;
    border-radius: 50%;
    color: #fff;
    background: #b5c5dc;
    cursor: not-allowed;
    box-shadow: none;
  }

  .nora-loading-composer p {
    margin: 14px 36px 0;
    color: #737d90;
    font-size: 11px;
    line-height: 1.25;
    text-align: center;
  }

  .nora-loading-counter {
    position: absolute;
    right: 12px;
    bottom: 8px;
    padding: 4px 7px;
    border-radius: 5px;
    color: #99a1af;
    background: #f2f4f7;
    font-size: 10px;
    font-weight: 600;
  }

  @media (prefers-reduced-motion: no-preference) {
    .nora-loading-line::after {
      animation: ${loadingShimmer} 1.25s ease-in-out infinite;
    }
  }

  @media (max-width: 600px) {
    grid-template-rows: 68px minmax(0, 1fr) 116px;

    .nora-loading-header {
      padding-left: 20px;
    }

    .nora-loading-header img {
      width: 34px;
      height: 34px;
    }

    .nora-loading-header strong {
      font-size: 22px;
    }

    .nora-loading-conversation {
      padding: 22px 18px;
    }

    .nora-loading-assistant-message {
      grid-template-columns: 18px minmax(0, 1fr);
      gap: 12px;
    }

    .nora-loading-message-bubble {
      min-height: 82px;
      padding: 18px;
    }

    .nora-loading-thinking {
      margin-left: 30px;
    }

    .nora-loading-composer {
      padding: 16px 14px 9px;
    }

    .nora-loading-controls {
      grid-template-columns: minmax(0, 1fr) 44px;
      gap: 10px;
    }

    .nora-loading-profile-select,
    .nora-loading-mic {
      display: none;
    }

    .nora-loading-input {
      height: 48px;
      font-size: 14px;
    }

    .nora-loading-send {
      width: 44px;
      height: 44px;
    }

    .nora-loading-composer p {
      margin: 12px 30px 0 0;
      font-size: 9px;
    }
  }
`
